import { Events, Platform } from 'ionic-angular'
import { VoidSyncEvent, SyncEvent } from 'ts-events'

import { SignalingMessage, SignalingServerConnectionStatus, DomainEvents, SignalingMessageType, MessageVisibleNotification } from '../../models/models'
import { ConfigProvider } from '../config/config'

/*
Interface for signaling providers.
*/
export abstract class AbstractSignalingProvider {
	/**
	Restituisce lo stato attuale di connessione (online, disconnesso - non registrato, offline)
	*/
	public get connectionStatus(): SignalingServerConnectionStatus {
		if(!this.isOnline) {
			return SignalingServerConnectionStatus.OFFLINE
		}
		if(!this.is_connected) {
			return SignalingServerConnectionStatus.DISCONNECTED
		}

		return SignalingServerConnectionStatus.CONNECTED
	}

	/**
	 * inizializzazione singleton
	*/
	protected static isInitialized: boolean = false;

	/**
	Risulta connesso
	*/
	public isOnline: boolean = false;

	public isEnabled: boolean = false;

	/**
	Si è connesso almeno una volta dall'avvio
	*/
	public isPreviouslyConnected: boolean = false;

	/**
	Peer locale chiamato
	*/
	public onCallInit: SyncEvent<SignalingMessage> = new SyncEvent<SignalingMessage>();
	/**
	Peer locale chiamante, UI di risposta aperte sul chiamato
	*/
	public onCallHandled: SyncEvent<string> = new SyncEvent<string>();
	/**
	Notifica chiusura chiamata
	*/
	public onCallHangup: SyncEvent<string> = new SyncEvent<string>();
	/**
	Server online
	*/
	public onOnline: VoidSyncEvent = new VoidSyncEvent;
	/**
	Server offline
	*/
	public onOffline: VoidSyncEvent = new VoidSyncEvent();
	/**
	id peer assegnato
	*/
	public onLogin: SyncEvent<string> = new SyncEvent<string>();
	/**
	id peer non assegnato
	*/
	public onLogout: SyncEvent<string> = new SyncEvent<string>();
	/**
	Ping ricevuto (risposto con pong)

	parametro: Payload per usi eventuali
	*/
	public onPingReceived: SyncEvent<any> = new SyncEvent<any>();
	/**
	Pong ricevuto (risposta a ping)

	parametro: Payload per usi eventuali
	*/
	public onPongReceived: SyncEvent<any> = new SyncEvent<any>();

	/**
	Connessione col server terminata e non riutilizzabile
	*/
	//public onFinalized: VoidSyncEvent = new VoidSyncEvent();
	/**
	Ricevuto messaggio da un altro peer
	*/
	public onMessageReceived: SyncEvent<SignalingMessage> = new SyncEvent<SignalingMessage>();

	/**
	Ricevuta richiesta di apertura portone - solo citofoni
	*/
	public onOpenDoorwayReceived: SyncEvent<SignalingMessage> = new SyncEvent<SignalingMessage>();
	/**
	Esito apertura portone - in risposta a onOpenDoorwayReceived
	*/
	public onOpenedDoorwayReceived: SyncEvent<SignalingMessage> = new SyncEvent<SignalingMessage>();


	/**
	Ricevuto messaggio di signaling
	*/
	public onSignaling: SyncEvent<any> = new SyncEvent<any>();

	private _peerId: string = null;
	/**
	L'id con cui si è connesso al server.
	Viene valorizzato al login utente o all'inizio della chiamata
	*/
	public get peerId(): string {
		return this._peerId
	}
	public set peerId(peerId: string) {
		if(this._peerId === peerId) {
			//caso banale: peer ID non variato
			return
		}

		if(this._peerId !== null) {
			if(peerId === null) {
				console.debug('[AbstractSignalingProvider.peerId set (null)] Rimozione del peer ID precedente: "' + this._peerId + '"')
			} else {
				console.debug('[AbstractSignalingProvider.peerId set ("' + peerId + '")] Sostituzione del peer ID precedente: "' + this._peerId + '"')
			}
		} else {//this._peerId === null e this._peerId !== peerID
			console.debug('[AbstractSignalingProvider.peerId set ("' + peerId + '")] Impostazione del peer ID per la prima volta')
		}

		this._peerId = peerId
	}
	/**
	L'id del peer con cui si sta comunicando
	*/
	public otherId: string = null;



	/**
	Rispecchia lo stati di autenticazione, nota: potrebbe essere autenticato ma non connesso, ovvero ha impostato solo id per la connessione
	*/
	public get isAuth(): boolean {
		return this.peerId !== null
	}

	/**
	E'registrato al server di signaling?
	*/
	public abstract get is_connected(): boolean

	constructor(
		protected events: Events,
		protected platform: Platform,
		//private _network: Network
	) {
		console.group('[AbstractSignalingProvider.new()] Nuovo provider di signaling')
		this.platform.ready().then(_type => {
			console.debug("[AbstractSignalingProvider.platform.ready()] Connessione e app inizializzate, connetto..")
			this.connect()
		})
		console.groupEnd()
		//consoleger.exit(methodName)
	}

	/**
	 * Connette al canale di scambio messaggi
	*/
	protected abstract connect(): Promise<void>
	/**
	 * Disconnette dal canale di scambio messaggi
	*/
	protected abstract disconnect(): void


	/**
	Deregistra il listener dagli eventi locali
	*/
	public detach(listener: any) {
		this.onOnline.detach(listener)
		this.onOffline.detach(listener)
		this.onLogin.detach(listener)
		this.onLogout.detach(listener)
		this.onMessageReceived.detach(listener)
		this.onSignaling.detach(listener)
		this.onCallHangup.detach(listener)
		this.onCallInit.detach(listener)
		this.onCallHandled.detach(listener)
		this.onOpenDoorwayReceived.detach(listener)
		this.onOpenedDoorwayReceived.detach(listener)
		this.onPingReceived.detach(listener)
		this.onPongReceived.detach(listener)
	}

	/**
	Chiamata presa in gestione dal peer remoto
	*/
	protected emitCallHandled(fromId: string) {
		//console.group('[AbstractSignalingProvider.emitCallHandled()] Propago ricezione CALL_HANDLED..')
		this.onCallHandled.post(fromId)
		//console.groupEnd()
	}

	/**
	Fine chiamata dal peer remoto
	*/
	protected emitCallHangup(fromId: string) {
		//console.group('[AbstractSignalingProvider.emitCallHangup()] Propago ricezione CALL_HANGUP..')
		this.onCallHangup.post(fromId)
		//console.groupEnd()
	}

	/**
	Chiamata in arrivo
	*/
	protected emitCallInit(callInit: SignalingMessage) {
		//console.group('[AbstractSignalingProvider.emitCallInit()] Propago ricezione CALL_INIT..')
		this.onCallInit.post(callInit)
		this.events.publish(DomainEvents.ServerSignalingCallIncoming, callInit)
		//console.groupEnd()
	}

	protected emitOnline() {
		//console.group('[AbstractSignalingProvider.emitOnline()] Propago evento di connessione al server di signaling..')
		this.onOnline.post()
		this.events.publish(DomainEvents.ServerSignalingOnline)
		//console.groupEnd()
	}

	protected emitOffline() {
		//console.group('[AbstractSignalingProvider.emitOffline()] Propago evento di disconnessione dal server di signaling..')
		this.onOffline.post()
		this.events.publish(DomainEvents.ServerSignalingOffline)
		//console.groupEnd()
	}

	protected emitLogin(peerId: string) {
		//console.group('[AbstractSignalingProvider.emitLogin()] Propago evento di registrazione al server di signaling..')
		this.onLogin.post(peerId)
		this.events.publish(DomainEvents.ServerSignalingLogin)
		//console.groupEnd()
	}

	protected emitLogoutRequestReceived(request: SignalingMessage) {
		//console.group('[AbstractSignalingProvider.emitLogout()] Propago evento di deregistrazione dal server di signaling..')
		this.events.publish(DomainEvents.USER_LOGOUT_REQUESTED, request)
		//console.groupEnd()
	}

	protected emitLogout(peerId: string) {
		//console.group('[AbstractSignalingProvider.emitLogout()] Propago evento di deregistrazione dal server di signaling..')
		this.onLogout.post(peerId)
		this.events.publish(DomainEvents.ServerSignalingLogout)
		//console.groupEnd()
	}

	/**
	Segnala la ricezione di una notifica di messaggi non letti
	*/
	protected emitMessageVisible(messageVisibleNotification: MessageVisibleNotification) {
		//console.group('[AbstractSignalingProvider.emitMessageVisible()] Propago evento di ricezione messaggio generico..')
		//this.onUnreadMessages.post(count)
		this.events.publish(DomainEvents.MESSAGE_VISIBLE_NOTIFICATION, messageVisibleNotification)
		//console.groupEnd()
	}

	protected emitOpenDoorwayReceived(receivedRequest: SignalingMessage) {
		//console.group('[AbstractSignalingProvider.emitOpenDoorwayReceived()] Propago evento di ricezione OPEN_DOORWAY..')
		this.events.publish(DomainEvents.OPEN_DOORWAY_REQUEST, receivedRequest)
		this.onOpenDoorwayReceived.post(receivedRequest)
		//console.groupEnd();
	}
	protected emitOpenedDoorwayReceived(receivedResult: SignalingMessage) {
		//console.group('[AbstractSignalingProvider.emitOpenDoorwayReceived()] Propago evento di ricezione OPENED_DOORWAY..')
		this.events.publish(DomainEvents.OPEN_DOORWAY_RESPONSE, receivedResult)
		this.onOpenedDoorwayReceived.post(receivedResult)
		//console.groupEnd();
	}


	protected emitPingReceived(ping: SignalingMessage) {
		//console.group('[AbstractSignalingProvider.emitPingReceived()] Propago evento di ricezione PING..', ping)
		this.events.publish(DomainEvents.SERVER_SIGNALING_PING, ping)
		this.onPingReceived.post(ping)
		//console.groupEnd()
	}

	protected emitPongReceived(pong: SignalingMessage) {
		//console.group('[AbstractSignalingProvider.emitPongReceived()] Propago evento di ricezione PONG..', pong)
		this.events.publish(DomainEvents.SERVER_SIGNALING_PONG, pong)
		this.onPongReceived.post(pong)
		//console.groupEnd()
	}

	/**
	Segnala la ricezione di un messaggio di signaling
	*/
	protected emitSignalingReceived(message: SignalingMessage) {
		//console.debug('[AbstractSignalingProvider.emitSignalingReceived()] Propago evento di ricezione messaggio generico..')
		this.onMessageReceived.post(message)
		this.events.publish(DomainEvents.ServerSignalingPeerMessageReceived, message)
	}

	/**
	Ricevuto messaggio di signaling
	*/
	protected emitSignaling(messageData: any) {
		//console.group('[AbstractSignalingProvider.emitSignaling()] Propago evento di ricezione SIGNALING..')
		this.onSignaling.post(messageData)
		//console.groupEnd()
	}

	/**
	 * Invocata dalla login dopo aver settato this.peerId
	 *
	 * Si mette in ascolto di eventi per il destinatario this.peerId
	 *
	 * Deve invocare on_message_received e on_message_visible
	*/
	protected abstract listen(): void

	/**
	 * Propaga evento di notifica numero messaggi non letti
	*/
	protected on_message_visible(value): void {
		//let messageVisibleNotification = node.val()//<MessageVisibleNotification>JSON.parse(node.val());
		console.debug('[AbstractSignalingProvider.on_message_available()] Notifica messaggi disponibili', value)

		if(!this.peerId) {
			console.warn("Il peer locale non risulta impostato all'arrivo del messaggio: Connessione non inizializzata correttamente")
		}

		try {
			//messaggio generico
			this.emitMessageVisible(value)
		} catch(error) {
			console.error('Errore propagando la ricezione del messaggio di signaling')
		}
	}
	/**
	 * Propaga gli eventi conseguenti la ricezione di un messaggio di signaling
	*/
	protected on_signaling_received(message) {
		message.receivedAt = new Date()
		console.debug("[AbstractSignalingProvider.on_signaling_received('message')] Ricevuto messaggio da " + message.senderId + ":", message)

		if(!this.peerId) {
			console.warn("Il peer locale non risulta impostato all'arrivo del messaggio: Connessione non inizializzata correttamente")
		}

		if(!message.senderId) {
			console.warn('Ricevuto un messaggio senza mittente:', message)
		}
		if(this.otherId && this.otherId != message.senderId) {
			console.debug('Ricevuto un messaggio da un mittente diverso dal precedente')
			/*
			XXX otherID è impostato all'invio di un messaggio, dunque non impone la mutua esclusione verso altri mittenti
			return;
			*/
		}

		if(!message.receiverId) {
			console.warn('Ricevuto un messaggio senza destinatario')
		} else if(message.receiverId != this.peerId) {
			console.warn('Ricevuto un messaggio che non era per questo destinatario, peer locale: ' + this.peerId)
			//scarta il messaggio
			return
		}

		try {
			//messaggio generico
			this.emitSignalingReceived(message)

			//messaggio specifico
			switch(message.type) {
				case SignalingMessageType.CALL_INIT:
					console.debug("[AbstractSignalingProvider.on_signaling_received('message')] CALL_INIT Richiesta di presa in gestione chiamata")
					this.emitCallInit(message)
					break
				case SignalingMessageType.CALL_HANDLED:
					console.debug("[AbstractSignalingProvider.on_signaling_received('message')] CALL_HANDLED Notifica di presa in gestione chiamata")
					this.emitCallHandled(message.senderId)
					break
				case SignalingMessageType.CALL_HANGUP:
					console.debug("[AbstractSignalingProvider.on_signaling_received('message')] CALL_HANGUP Notifica di fine chiamata")
					this.emitCallHangup(message.senderId)
					break
				case SignalingMessageType.LOGOUT_REQUEST:
					console.debug("[AbstractSignalingProvider.on_signaling_received('message')] LOGOUT_REQUEST Richiesta di logout")
					this.emitLogoutRequestReceived(message)
					break
				case SignalingMessageType.SIGNALING:
					console.debug("[AbstractSignalingProvider.on_signaling_received('message')] SIGNALING Messaggio di signaling webrtc")
					this.emitSignaling(message.data)
					break
				case SignalingMessageType.OPEN_DOORWAY:
					console.debug("[AbstractSignalingProvider.on_signaling_received('message')] OPEN_DOORWAY Richiesta apertura portone")
					this.emitOpenDoorwayReceived(message)
					break
				case SignalingMessageType.OPENED_DOORWAY:
					console.debug("[AbstractSignalingProvider.on_signaling_received('message')] OPENED_DOORWAY Esito richiesta apertura portone")
					this.emitOpenedDoorwayReceived(message)
					break
				case SignalingMessageType.PING:
					console.debug("[AbstractSignalingProvider.on_signaling_received('message')] PING")
					this.emitPingReceived(message)
					//RISPOSTA AUTOMATICA CON PONG
					this.sendPong(message)
					break
				case SignalingMessageType.PONG:
					console.debug("[AbstractSignalingProvider.on_signaling_received('message')] PONG")
					this.emitPongReceived(message)
					break
				default:
					console.warn("[AbstractSignalingProvider.nodeSignaling.on('child_added')] Ricevuto messaggio non gestito: " + message.type)
					break
			}
		} catch(error) {
			console.error('Errore propagando la ricezione del messaggio di signaling', error)
		}
	}
	/**
	Si registra al server di signaling iniziando a ricevere i messaggi per l'id specificato

	(ri)connette al database se non già connesso o autenticato diversamente
	*/
	public login(myId: string): Promise<void> {
		let self = this
		console.debug('AbstractSignalingProvider.login(' + myId + ')] Setup id login per la connessione al server di signaling:' + myId)

		if(this.is_connected) {
			//controllo se già autenticato
			if(this.isAuth && this.peerId != myId) {
				console.debug('login già effettuato con altro ID, effettuo prima il logout..')
				self.logout()
			}
		}

		//salva l'id del peer locale
		this.peerId = myId

		//ascolto sul canale, dipendente dall'implementazione specifica
		this.listen()

		//notifica avvenuto login
		this.emitLogin(myId)

		console.groupEnd()

		return Promise.resolve()
	}

	/**
	 * Si disconnette in modo pulito dal canale di signaling
	*/
	protected abstract delisten()

	/**
	Se il database è inizializzato ed è autenticato (listening messaggi in arrivo) effettua il logout (delistening e chiusura connessione al database)
	*/
	public logout() {
		console.group('[AbstractSignalingProvider.logout()] Deregistrazione dal server di signaling')


		if(!this.isAuth) {
			console.debug('Non autenticato: Ignoro la richiesta di logout')
			console.groupEnd()
			return
		}

		this.delisten()

		let peerId = this.peerId
		this.peerId = null

		//Reset dell'altro peer
		this.otherId = null

		//propaga l'evento di avvenuto logout
		this.emitLogout(peerId)

		console.groupEnd()
	}


	/**
	Forza riconnessione ed eventualmente rilogin
	*/
	public reconnect() {
		console.group('[AbstractSignalingProvider.reconnect()] Rinnovo connessione e autenticazione')

		let peerId = this.peerId

		this.logout()

		this.disconnect()

		this.connect()

		if(peerId) {
			console.debug('Autenticato in precedenza: Riautentico')
			this.login(peerId)
		}

		console.groupEnd()
	}

	/**
	 * Invia fisicamente un messaggio sul canale
	*/
	protected abstract send_channel(message: SignalingMessage): Promise<SignalingMessage>
	/**
	Invia il messaggio attraverso il canale implementato
	*/
	public send(message: SignalingMessage): Promise<SignalingMessage> {
		console.debug('[AbstractSignalingProvider.send(message)] Invio messaggio di signaling: type=' + message.type + '; receiverId="' + message.receiverId + '"', message)
		//se manca aggiunge l'id mittente
		if(!message.senderId) {
			message.senderId = this.peerId//viene valorizzato al login
		}

		if(!message.receiverId) {
			//console.debug("Messaggio senza receiverId, uso this.otherId: " + this.otherId)
			//XXX esiste il caso login e logout che non danno errore
			/* if(!this.otherId) {
				console.error('this.otherId non salvato, impossibile inviare il messaggio')
			} */
			message.receiverId = this.otherId
		}

		//se non ha destinatario errore
		if(!message.receiverId) {
			console.info("Messaggio senza destinatario", message)
			//console.groupEnd()
			//return Promise.reject("Messaggio senza destinatario, impossibile procedere all'invio")
		}

		message.sentAt = new Date()
		return this.send_channel(message)
	}


	/**
	(chiamato) Chiamata presa in gestione, imposta contemporaneamente l'id del ricevente
	*/
	public sendCallHandled(otherId: string): Promise<SignalingMessage> {
		console.debug("[AbstractSignalingProvider.sendCallHandled(" + otherId + ")] Invio CALL_HANDLED Notifica di presa in gestione chiamata e imposto l'interlocutore, otherId=" + otherId)
		this.otherId = otherId

		return this.send({
			type: SignalingMessageType.CALL_HANDLED
		})
	}

	/**
	(chiamato) Chiamata presa in gestione
	*/
	public sendCallHangup(message: string = null): Promise<SignalingMessage> {
		console.debug('[AbstractSignalingProvider.sendHangup("' + message + '")] Invio CALL_HANGUP Notifica di chiusura chiamata')
		return this.send({
			type: SignalingMessageType.CALL_HANGUP,
			data: message
		})
	}

	/**
	(chiamante) Richiesta di gestione chiamata, imposta contemporaneamente l'id del ricevente
	*/
	public sendCallInit(otherId: string, housingUnitId: number = null): Promise<SignalingMessage> {
		console.debug("[AbstractSignalingProvider.sendCallInit(" + otherId + ")] Invio CALL_INIT Richiesta di presa in gestione chiamata e imposto l'interlocutore")
		this.otherId = otherId

		return this.send({
			receiverId: otherId,
			housingUnitId: housingUnitId,
			type: SignalingMessageType.CALL_INIT
		})
	}

	public sendLogoutRequest(userId: number, exceptDeviceIdentifier: string = null): any {
		console.debug("[AbstractSignalingProvider.sendLogout(" + userId + "," + exceptDeviceIdentifier + ")] Invio LOGOUT_REQUEST Richiesta di logout con eventuale eccezione per un singolo dispositivo")

		let receiverId = ConfigProvider.generatePeerIdUser(userId)

		return this.send({
			receiverId,//peer sullo stesso utente
			deviceIdentifier: exceptDeviceIdentifier,//questo device
			userId: userId,//id utente che deve fare logout
			type: SignalingMessageType.LOGOUT_REQUEST
		})
	}

	/**
	Invia PING per verificare se l'altro peer è connesso
	*/
	public sendPing(receiverId: string): Promise<SignalingMessage> {
		console.debug("[AbstractSignalingProvider.sendPing(otherId, payload)] Invio PING a: " + this.otherId)

		let senderId = this.peerId

		return this.send({
			senderId,
			receiverId,
			type: SignalingMessageType.PING,
		})
	}

	/**
	Invia PONG in risposta a PING

	Usato solo internamente in automatico all'arrivo del PING
	*/
	private sendPong(pingMessage: SignalingMessage): Promise<SignalingMessage> {
		console.debug("[AbstractSignalingProvider.sendPong(pingMessage)] Invio PONG risposta a PING..", pingMessage)
		return this.send({
			request: pingMessage,
			receiverId: pingMessage.senderId,
			type: SignalingMessageType.PONG,
			data: pingMessage.data
		})
	}

	/**
Invia richiesta di apertura portone
	*/
	public sendOpenDoorway(otherId: string) {
		console.debug("[AbstractSignalingProvider.sendOpenDoorway(otherId)] Invio OPEN_DOORWAY richiesta di apertura portone a:", otherId)
		return this.send({
			receiverId: otherId,
			type: SignalingMessageType.OPEN_DOORWAY
		})
	}
	/**
Invia esito rischiesta apertura portone
	*/
	public sendOpenedDoorway(originalRequest: SignalingMessage, result: any) {
		console.debug("[AbstractSignalingProvider.sendOpenDoorway(openMessage)] Invio OPENED_DOORWAY Esito richiesta di apertura portone..", result)
		return this.send({
			request: originalRequest,
			receiverId: originalRequest.senderId,
			type: SignalingMessageType.OPENED_DOORWAY,
			data: result
		})
	}
}



// WEBPACK FOOTER //
// ./src/providers/signaling/abstract.ts